import { isSelf } from '../model/Policyholder';

/**
 * Do we hold enough about who pays for this coverage to bill on it?
 *
 * A FOURTH AXIS, and the one this bet adds. The other three are in `constants/coverage`: whether the
 * coverage is in force, whether the payer has confirmed the plan is active, and whether we have
 * chosen to pause billing. Readiness is none of those. It asks about OUR OWN RECORD rather than the
 * payer's answer or our intent, and a coverage can be in force, verified, unpaused and still unready
 * because nobody has the parent's date of birth.
 *
 * IT NEVER BLOCKS A SAVE. That is what separates it from `validateCoverage`, which says a coverage
 * cannot be recorded at all. A card arrives with a name and a member id and little else, and refusing
 * the record produces placeholder birthdays, which are worse than blanks because nothing downstream
 * can tell them from real ones. So this reports, and the editor shows what is missing.
 *
 * CONDITIONAL ON WHO THE PATIENT IS, which is the part a flat list gets wrong in both directions. A
 * payer asks for more about a policyholder who is also the patient than about one who is not. Ask for
 * the parent's gender and address and coverage holds on facts no claim needs; skip the member id and
 * it passes a coverage every payer will reject.
 *
 * SPANS THE COVERAGE AS WELL AS THE PERSON. The member id is a fact about the policy, not about
 * whoever holds it, so it lives on the coverage. Readiness still needs it, which is why this takes
 * both and not just demographics.
 *
 * WHAT IT DELIBERATELY DOES NOT CHECK. When somebody other than the client holds the policy, the
 * payer wants the gender and address of the PATIENT, which is the client's own record rather than
 * anything this bet introduced. That is the client editor's completeness to enforce, and reporting it
 * on a coverage row would send somebody to the wrong screen to fix it.
 *
 * MIRRORED BY THE PROJECTOR, and the mirroring is the point: the editor promises "this will bill once
 * these arrive", and only the projector can keep that promise. It runs the same rule over the same
 * fields on the billing branch. If the two drift, the editor lies. Same relationship as
 * `coverageBilling.isBillableNow` and the coverage resolver, and the same warning applies.
 */

/**
 * What is missing, as codes. The domain does not know about i18n; the editor maps these.
 *
 * The address is ONE requirement rather than one per part, because a payer needs a deliverable
 * address and naming four blanks separately would read as four problems. Only the line and the city
 * are asked for: whether an address also carries a subdivision or a postal code is a property of the
 * country, the domain does not know countries, and encoding a country table here would duplicate the
 * one the form already uses in a layer that must not reach for it.
 */
export const POLICYHOLDER_READINESS = {
  MISSING_LAST_NAME: 'missingLastName',
  MISSING_DATE_OF_BIRTH: 'missingDateOfBirth',
  MISSING_GENDER: 'missingGender',
  MISSING_ADDRESS: 'missingAddress',
  MISSING_MEMBER_ID: 'missingMemberId',
};

/**
 * Whitespace counts as missing. The demographics arrive trimmed from the mapper, but the member id
 * comes straight off a text field, so a stray space would make a coverage ready on a value no payer
 * can match.
 */
const absent = (value) => {
  const given = typeof value === 'string' ? value.trim() : value;

  return given === null || given === undefined || given === '';
};

/**
 * @param {Object} coverage
 * @param {import('../model/Policyholder').Policyholder} coverage.policyholder
 * @param {string|null} coverage.memberId
 * @param {import('../model/PolicyholderDemographics').PolicyholderDemographics|null} demographics -
 *   whoever holds the policy, which is the client themselves when the relationship is self. Null
 *   while the record is still being read, which reports nothing rather than reporting everything.
 * @returns {string[]} POLICYHOLDER_READINESS codes; empty when the coverage can bill
 */
export function policyholderReadiness({ policyholder, memberId }, demographics) {
  // Nothing known yet is not the same as nothing on file. A read in flight would otherwise flash
  // every requirement at once and then clear, which reads as a problem that fixed itself.
  if (demographics === null || demographics === undefined) {
    return [];
  }

  const missing = [];

  if (absent(demographics.lastName)) {
    missing.push(POLICYHOLDER_READINESS.MISSING_LAST_NAME);
  }
  if (absent(demographics.dateOfBirth)) {
    missing.push(POLICYHOLDER_READINESS.MISSING_DATE_OF_BIRTH);
  }
  if (absent(memberId)) {
    missing.push(POLICYHOLDER_READINESS.MISSING_MEMBER_ID);
  }

  // Only when the policyholder is also the patient. For anyone else these belong to the client.
  if (isSelf(policyholder)) {
    if (absent(demographics.gender)) {
      missing.push(POLICYHOLDER_READINESS.MISSING_GENDER);
    }
    if (absent(demographics.addressLine1) || absent(demographics.city)) {
      missing.push(POLICYHOLDER_READINESS.MISSING_ADDRESS);
    }
  }

  return missing;
} // policyholderReadiness()

/**
 * Can this coverage bill, as far as our own record is concerned?
 *
 * Says nothing about whether it WILL bill: that also needs the coverage in force, eligibility
 * confirmed and billing unpaused. See `coverageBilling`.
 *
 * @returns {boolean}
 */
export function isReady(coverage, demographics) {
  return policyholderReadiness(coverage, demographics).length === 0;
} // isReady()
